import type { ReactNode } from 'react';

import type { MenuItemState } from '~/chords/KebabMenu/components/KebabMenuItem/KebabMenuItem';
import type { RenderDropdownProps } from '~/chords/Select/components/MultiSelect/MultiSelect';
import { useMultiSelect } from '~/chords/Select/components/MultiSelect/useMultiSelect';

import { SelectedItemRows } from './components/SelectedItemRows';

export type { RenderDropdownProps };

export interface SortableMultiSelectSecondaryAction<T> {
  label: string;
  onAction: (item: T) => void;
  state?: MenuItemState;
  availableInReadOnly?: boolean;
}

export interface SortableMultiSelectProps<T> {
  value: T[];
  onValueChange: (items: T[]) => void;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string;
  getItemImage?: (item: T) => string | undefined;
  renderHeader?: (item: T) => ReactNode;
  /** @deprecated Use renderHeader instead */
  renderContent?: (item: T) => ReactNode;
  renderBody?: (item: T) => ReactNode;
  secondaryActions?: SortableMultiSelectSecondaryAction<T>[];
  renderDropdown: (props: RenderDropdownProps<T>) => ReactNode;
  removeLabel?: string;
  disabled?: boolean;
}

export function SortableMultiSelect<T>({
  value,
  onValueChange,
  getItemLabel,
  getItemValue,
  getItemImage,
  renderHeader,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  renderContent,
  renderBody,
  secondaryActions,
  renderDropdown,
  removeLabel,
  disabled = false,
}: SortableMultiSelectProps<T>) {
  const resolvedRenderHeader = renderHeader ?? renderContent;
  const { selectedIds, resetKey, handleSelect, handleRemove } = useMultiSelect({
    selectedValues: value,
    onValueChange,
    getItemValue,
    disabled,
  });

  const handleReorder = (reorderedItems: T[]) => {
    if (disabled) {
      return;
    }

    onValueChange(reorderedItems);
  };

  const resolvedSecondaryActions = secondaryActions
    ? (item: T) =>
        secondaryActions.map((action) => ({
          label: action.label,
          onAction: () => action.onAction(item),
          state: action.state,
          availableInReadOnly: action.availableInReadOnly,
        }))
    : undefined;

  return (
    <div className="flex flex-col gap-sm">
      <div className="flex justify-end">
        {renderDropdown({
          onSelect: handleSelect,
          selectedValues: selectedIds,
          resetKey,
          disabled,
        })}
      </div>
      <SelectedItemRows<T>
        items={value}
        getItemValue={getItemValue}
        getItemLabel={getItemLabel}
        getItemImage={getItemImage}
        renderHeader={resolvedRenderHeader}
        renderBody={renderBody}
        secondaryActions={resolvedSecondaryActions}
        onRemove={handleRemove}
        onReorder={handleReorder}
        removeLabel={removeLabel}
        disabled={disabled}
      />
    </div>
  );
}

SortableMultiSelect.displayName = 'SortableMultiSelect';
